import React, { useContext, useEffect, useState } from 'react';
import { companyPageContext } from '@/components/NewCompanyPage/NewCompanyPage';
import API_STATES from '@/constants/StateConstants';
import { handleApiError, noOp } from '@/components/General/HandleApiError';
import ByzzerSwitch from '@/components/General/ByzzerSwitch';
import generalStyles from '@/components/General/Styles.module.scss';
import {
    updateCompanyDodSuperuserStatus,
    updateCompanyIsOboModeAllowedStatus,
    updateCompanyTestAccountStatus,
    updateDisableCompanyStatus,
    getCompanyMultiTenantSettings,
    editCompanyMultiTenantSettings,
} from '@/services/api/company.service';
import store from '@/store';
import styles from '@/components/NewCompanyPage/NewCompanyPage.module.scss';

function ToggleStatus({ apiState, apiError }) {
    if (apiState === API_STATES.loading) {
        return <span className={generalStyles.statusMessage}>Processing...</span>;
    }

    if (apiState === API_STATES.error) {
        return <span className={generalStyles.errorMessage}>Failed to Update: {apiError}</span>;
    }

    if (apiState === API_STATES.success) {
        return <span className={generalStyles.successMessage}>saved!</span>;
    }

    return null;
}

function ToggleRow({ id, label, children, status }) {
    return (
        <div className={styles.companyToggleItem}>
            <div className={styles.companyToggleControl}>
                <label htmlFor={id}>{label}</label>
                {children}
            </div>
            {status && <div className={styles.companyToggleStatus}>{status}</div>}
        </div>
    );
}

export default function CompanyFeatureToggles({ onChange = noOp }) {
    const {
        nsCompanyId,
        companyConfig: { isOboModeAllowed, isTestAccount, isDisabled, isDodSuperuser },
    } = useContext(companyPageContext);
    const { isCsr, isSuperUser, isSales, isProductSuperUser } = store?.getState()?.account ?? {};

    const canEditCompanyDisabledStatus = isCsr || isSuperUser || isSales;
    const canEditMultiTenantSettings = isCsr || isSuperUser;

    const [isMultiTenant, setIsMultiTenant] = useState(null);
    const [multiTenantAgreementLink, setMultiTenantAgreementLink] = useState('');
    const [isLoadingMultiTenantSettings, setIsLoadingMultiTenantSettings] = useState(true);
    const [updateMultiTenantApiState, setUpdateMultiTenantApiState] = useState(API_STATES.none);
    const [updateMultiTenantApiError, setUpdateMultiTenantApiError] = useState('');

    const [oboModeStatus, setOboModeStatus] = useState(null);
    const [testAccountStatus, setTestAccountStatus] = useState(null);
    const [dodSuperuserStatus, setDodSuperuserStatus] = useState(null);
    const [confirmDisableAuth, setConfirmDisableAuth] = useState(false);

    const [updateOboModeStatusApiState, setUpdateOboModeStatusApiState] = useState(API_STATES.none);
    const [updateOboModeStatusApiError, setUpdateOboModeStatusApiError] = useState('');
    const [updateTestAccountStatusApiState, setUpdateTestAccountStatusApiState] = useState(API_STATES.none);
    const [updateTestAccountStatusApiError, setUpdateTestAccountStatusApiError] = useState('');
    const [updateDodSuperuserStatusApiState, setUpdateDodSuperuserStatusApiState] = useState(API_STATES.none);
    const [updateDodSuperuserStatusApiError, setUpdateDodSuperuserStatusApiError] = useState('');
    const [confirmDisableAuthStatus, setConfirmDisableAuthStatus] = useState(API_STATES.none);
    const [confirmDisableAuthError, setConfirmDisableAuthError] = useState('');

    useEffect(() => {
        let isMounted = true;
        const loadMultiTenantSettings = async () => {
            if (!nsCompanyId) {
                setIsLoadingMultiTenantSettings(false);
                setIsMultiTenant(null);
                setMultiTenantAgreementLink('');
                return;
            }

            setIsLoadingMultiTenantSettings(true);
            const res = await getCompanyMultiTenantSettings(nsCompanyId);
            if (!isMounted) {
                return;
            }
            if (res.status === API_STATES.success) {
                setIsMultiTenant(res.data.multiTenantEnabled);
                setMultiTenantAgreementLink(res.data.multiTenantAgreement ?? '');
            }
            setIsLoadingMultiTenantSettings(false);
        };

        loadMultiTenantSettings();

        return () => {
            isMounted = false;
        };
    }, [nsCompanyId]);

    const updateMultiTenantStatus = async () => {
        const nextValue = !isMultiTenant;

        setUpdateMultiTenantApiState(API_STATES.loading);
        setIsMultiTenant(nextValue);
        const response = await editCompanyMultiTenantSettings(nsCompanyId, nextValue, multiTenantAgreementLink);
        if (response.status === API_STATES.success) {
            setUpdateMultiTenantApiState(API_STATES.success);
            onChange();
        } else {
            handleApiError(response.error, setUpdateMultiTenantApiState, setUpdateMultiTenantApiError);
        }
        setTimeout(() => setUpdateMultiTenantApiState(API_STATES.none), 3000);
    };

    useEffect(() => {
        setOboModeStatus(isOboModeAllowed);
    }, [isOboModeAllowed]);

    useEffect(() => {
        setTestAccountStatus(isTestAccount);
    }, [isTestAccount]);

    useEffect(() => {
        setDodSuperuserStatus(isDodSuperuser);
    }, [isDodSuperuser]);

    useEffect(() => {
        setConfirmDisableAuth(isDisabled);
    }, [isDisabled]);

    const updateOboModeStatus = async () => {
        const nextOboModeStatus = !oboModeStatus;

        setUpdateOboModeStatusApiState(API_STATES.loading);
        setOboModeStatus(nextOboModeStatus);
        const response = await updateCompanyIsOboModeAllowedStatus(nsCompanyId, nextOboModeStatus);
        if (response.status === API_STATES.success) {
            setUpdateOboModeStatusApiState(API_STATES.success);
            onChange();
        } else {
            handleApiError(response.error, setUpdateOboModeStatusApiState, setUpdateOboModeStatusApiError);
        }
        setTimeout(() => setUpdateOboModeStatusApiState(API_STATES.none), 3000);
    };

    const updateTestAccountStatus = async () => {
        const nextTestAccountStatus = !testAccountStatus;

        setUpdateTestAccountStatusApiState(API_STATES.loading);
        setTestAccountStatus(nextTestAccountStatus);
        const response = await updateCompanyTestAccountStatus(nsCompanyId, nextTestAccountStatus);
        if (response.status === API_STATES.success) {
            setUpdateTestAccountStatusApiState(API_STATES.success);
            onChange();
        } else {
            handleApiError(response.error, setUpdateTestAccountStatusApiState, setUpdateTestAccountStatusApiError);
        }
        setTimeout(() => setUpdateTestAccountStatusApiState(API_STATES.none), 3000);
    };

    const updateDodSuperuserStatus = async () => {
        const nextDodSuperuserStatus = !dodSuperuserStatus;

        setUpdateDodSuperuserStatusApiState(API_STATES.loading);
        setDodSuperuserStatus(nextDodSuperuserStatus);
        const response = await updateCompanyDodSuperuserStatus(nsCompanyId, nextDodSuperuserStatus);
        if (response.status === API_STATES.success) {
            setUpdateDodSuperuserStatusApiState(API_STATES.success);
            onChange();
        } else {
            handleApiError(response.error, setUpdateDodSuperuserStatusApiState, setUpdateDodSuperuserStatusApiError);
        }
        setTimeout(() => setUpdateDodSuperuserStatusApiState(API_STATES.none), 3000);
    };

    const onDisableCompany = async () => {
        const nextDisableAuthStatus = !confirmDisableAuth;

        setConfirmDisableAuthStatus(API_STATES.loading);
        setConfirmDisableAuth(nextDisableAuthStatus);
        const result = await updateDisableCompanyStatus(nsCompanyId, nextDisableAuthStatus);
        if (result.status === API_STATES.success) {
            setConfirmDisableAuthStatus(API_STATES.success);
            onChange();
        } else {
            handleApiError(result.error, setConfirmDisableAuthStatus, setConfirmDisableAuthError);
        }
        setTimeout(() => setConfirmDisableAuthStatus(API_STATES.none), 3000);
    };

    return (
        <div className={styles.companyToggleList} data-test="company-feature-toggle-list">
            <ToggleRow
                id="dod-superuser-switch"
                label="DOD Superuser"
                status={
                    updateDodSuperuserStatusApiState !== API_STATES.none ? (
                        <ToggleStatus
                            apiState={updateDodSuperuserStatusApiState}
                            apiError={updateDodSuperuserStatusApiError}
                        />
                    ) : null
                }
            >
                <ByzzerSwitch
                    data-test="dod-superuser-switch"
                    id="dod-superuser-switch"
                    onChange={updateDodSuperuserStatus}
                    value={dodSuperuserStatus ?? false}
                    disabled={!isProductSuperUser}
                />
            </ToggleRow>
            <ToggleRow
                id="test-account-switch"
                label="Test Account"
                status={
                    updateTestAccountStatusApiState !== API_STATES.none ? (
                        <ToggleStatus
                            apiState={updateTestAccountStatusApiState}
                            apiError={updateTestAccountStatusApiError}
                        />
                    ) : null
                }
            >
                <ByzzerSwitch
                    data-test="test-account-switch"
                    id="test-account-switch"
                    onChange={updateTestAccountStatus}
                    value={testAccountStatus ?? false}
                    disabled={!isSuperUser}
                />
            </ToggleRow>
            <ToggleRow
                id="obo-mode-switch"
                label="Allow Obo Mode"
                status={
                    updateOboModeStatusApiState !== API_STATES.none || oboModeStatus == null ? (
                        <>
                            {updateOboModeStatusApiState !== API_STATES.none && (
                                <ToggleStatus apiState={updateOboModeStatusApiState} apiError={updateOboModeStatusApiError} />
                            )}
                            {oboModeStatus == null && (
                                <span className={generalStyles.statusMessage}>Company is missing obo mode configuration</span>
                            )}
                        </>
                    ) : null
                }
            >
                <ByzzerSwitch
                    data-test="obo-mode-switch"
                    id="obo-mode-switch"
                    onChange={updateOboModeStatus}
                    value={oboModeStatus ?? false}
                    disabled={!isSuperUser}
                />
            </ToggleRow>
            <ToggleRow
                id="disable-company-switch"
                label="Disable Authentication"
                status={
                    confirmDisableAuthStatus !== API_STATES.none ? (
                        <ToggleStatus apiState={confirmDisableAuthStatus} apiError={confirmDisableAuthError} />
                    ) : null
                }
            >
                <ByzzerSwitch
                    data-test="disable-company-switch"
                    id="disable-company-switch"
                    onChange={onDisableCompany}
                    value={confirmDisableAuth ?? false}
                    disabled={!canEditCompanyDisabledStatus}
                />
            </ToggleRow>
            <ToggleRow
                id="multi-company-login-switch"
                label="Multi Company Login"
                status={
                    updateMultiTenantApiState !== API_STATES.none ? (
                        <ToggleStatus apiState={updateMultiTenantApiState} apiError={updateMultiTenantApiError} />
                    ) : null
                }
            >
                <ByzzerSwitch
                    data-test="multi-company-login-switch"
                    id="multi-company-login-switch"
                    onChange={updateMultiTenantStatus}
                    value={isMultiTenant ?? false}
                    disabled={!canEditMultiTenantSettings || isLoadingMultiTenantSettings}
                />
            </ToggleRow>
        </div>
    );
}